import { DailyInstructorGrid, type DayGridResponse } from "./DailyInstructorGrid";
import type { NewAppointmentSlotContext } from "./NewAppointmentModal";
import type { AppointmentEditContext } from "./WeeklyCalendar";
import type { AppointmentDragDropTarget } from "./appointmentDrag";
import "./DailyWeekStack.css";

export type WeekDayGrid = {
  iso: string;
  weekday: string;
  dmY: string;
  isToday: boolean;
  data: DayGridResponse;
};

type Props = {
  days: WeekDayGrid[];
  onReserve: (ctx: NewAppointmentSlotContext) => void;
  onOpenLessonReport: (appt: {
    id: string;
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
    student_id?: string;
    student_name?: string | null;
  }) => void;
  onShowAuditHistory: (appointmentId: string) => void;
  onEditReservation?: (ctx: AppointmentEditContext) => void;
  onCancelAppointment?: (appt: {
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
    status: string;
  }) => void;
  onReactivateAppointment?: (appt: {
    id: string;
    student_name?: string | null;
    starts_at: string;
    ends_at: string;
  }) => void;
  onAppointmentDragDrop?: (target: AppointmentDragDropTarget) => void;
  onScrollToDay?: (iso: string) => void;
};

function slotColumnsSignature(data: WeekDayGrid["data"]): string {
  return data.slot_columns.map((c) => `${c.start}-${c.end}`).join("|");
}

export function DailyWeekStack({
  days,
  onReserve,
  onOpenLessonReport,
  onShowAuditHistory,
  onEditReservation,
  onCancelAppointment,
  onReactivateAppointment,
  onAppointmentDragDrop,
  onScrollToDay,
}: Props) {
  const signatures = days.map((d) => slotColumnsSignature(d.data));
  const hasMixedTemplates = new Set(signatures).size > 1;

  return (
    <div className="kp-daily-week-stack">
      {hasMixedTemplates && (
        <p className="kp-daily-week-stack__mixed-note" role="status">
          Bu haftada Günlere gore farkli slot şablonu uygulaniyor (gecerlilik tarihine gore).
        </p>
      )}
      {days.map((day) => {
        const effectiveFrom = day.data.settings?.effective_from?.slice(0, 10);
        const isTemplateStartDay = effectiveFrom === day.iso;
        const slotCount = day.data.slot_columns.length;
        const firstSlot = day.data.slot_columns[0];
        const lastSlot = day.data.slot_columns[day.data.slot_columns.length - 1];
        const timeRange =
          firstSlot && lastSlot ? `${firstSlot.start}–${lastSlot.end}` : null;

        return (
        <section
          key={day.iso}
          id={`kp-cal-day-${day.iso}`}
          className={`kp-daily-week-stack__day${day.isToday ? " kp-daily-week-stack__day--today" : ""}`}
        >
          <header className="kp-daily-week-stack__head">
            {onScrollToDay && <span className="kp-daily-week-stack__head-spacer" aria-hidden />}
            <div className="kp-daily-week-stack__head-main">
              <h2 className="kp-daily-week-stack__title">
                <span className="kp-daily-week-stack__title-line">
                  {day.weekday} | {day.dmY}
                  {timeRange && (
                    <span className="kp-daily-week-stack__slots-inline">
                      ({slotCount} slot · {timeRange})
                    </span>
                  )}
                  {day.isToday && (
                    <span className="kp-daily-week-stack__today">BUGÜN</span>
                  )}
                  {isTemplateStartDay && (
                    <span className="kp-daily-week-stack__template-badge">Yeni şablon</span>
                  )}
                </span>
              </h2>
            </div>
            {onScrollToDay ? (
              <button
                type="button"
                className="kp-daily-week-stack__focus"
                onClick={() => onScrollToDay(day.iso)}
              >
                Günlük görünüme git →
              </button>
            ) : (
              <span className="kp-daily-week-stack__head-spacer" aria-hidden />
            )}
          </header>
          <DailyInstructorGrid
            data={day.data}
            onReserve={onReserve}
            onOpenLessonReport={onOpenLessonReport}
            onShowAuditHistory={onShowAuditHistory}
            onEditReservation={onEditReservation}
            onCancelAppointment={onCancelAppointment}
            onReactivateAppointment={onReactivateAppointment}
            onAppointmentDragDrop={onAppointmentDragDrop}
          />
        </section>
        );
      })}
    </div>
  );
}
